<script>
  import Icon from "./Icon.svelte";

  /**
   * Reusable Checkbox Component
   * @prop {string} id - Checkbox ID
   * @prop {string} label - Label text (required for accessibility)
   * @prop {boolean} checked - Checked state (bindable)
   * @prop {boolean} required - Required field
   * @prop {boolean} disabled - Disabled state
   * @prop {string} error - Error message
   * @prop {string} icon - Optional icon name (Lucide icon)
   */
  let {
    id = "",
    label = "",
    checked = $bindable(false),
    required = false,
    disabled = false,
    error = "",
    icon = "",
    onchange = undefined,
  } = $props();
</script>

<div class="checkbox-group" class:has-error={error}>
  <label class="checkbox-label" for={id}>
    <input
      type="checkbox"
      {id}
      class="checkbox-input"
      bind:checked
      {required}
      {disabled}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {onchange}
    />
    <span class="checkbox-box">
      <svg class="checkbox-icon" viewBox="0 0 16 16" fill="none">
        <path
          d="M3.5 8.5L6.5 11.5L12.5 5.5"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <span class="checkbox-text">
      {#if icon}<Icon name={icon} size={16} className="field-icon" />{/if}
      {label}
      {#if required}<span class="required">*</span>{/if}
    </span>
  </label>

  {#if error}
    <p id="{id}-error" class="form-error">{error}</p>
  {/if}
</div>

<style>
  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    cursor: pointer;
    min-height: var(--touch-target-min);
    padding: var(--space-2) 0;
  }

  .checkbox-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .checkbox-box {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border: 2px solid var(--color-gray-300);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-fast);
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-sm));
  }

  .checkbox-icon {
    width: 14px;
    height: 14px;
    opacity: 0;
    color: white;
    transition:
      opacity var(--transition-fast),
      transform var(--transition-fast);
    transform: scale(0.5);
  }

  .checkbox-input:checked + .checkbox-box {
    background: var(--gradient-primary);
    border-color: transparent;
    box-shadow: 0 2px 6px rgba(124, 58, 237, 0.3);
  }

  .checkbox-input:checked + .checkbox-box .checkbox-icon {
    opacity: 1;
    transform: scale(1);
  }

  .checkbox-input:focus-visible + .checkbox-box {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
    border-color: var(--color-primary-400);
  }

  .checkbox-label:hover .checkbox-box {
    border-color: var(--color-gray-400);
  }

  .checkbox-input:checked + .checkbox-box:hover {
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.35);
  }

  .checkbox-input:disabled + .checkbox-box {
    background: var(--color-gray-100);
    border-color: var(--color-gray-200);
    cursor: not-allowed;
    backdrop-filter: none;
  }

  .checkbox-input:disabled ~ .checkbox-text {
    color: var(--color-gray-400);
    cursor: not-allowed;
  }

  .checkbox-text {
    font-size: var(--text-base);
    color: var(--color-gray-700);
    line-height: 1.4;
    padding-top: 1px;
  }

  .required {
    color: var(--color-primary-500);
    margin-left: var(--space-1);
  }

  .has-error .checkbox-box {
    border-color: var(--color-error-400);
  }

  .form-error {
    font-size: var(--text-sm);
    color: var(--color-error-600);
    margin: 0;
    margin-left: calc(22px + var(--space-3));
  }
</style>
